import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, ListGroup, Modal, Spinner } from 'react-bootstrap';
import { atlasApi } from './AtlasApi';

export default function AtlasMapsPopup({ atlas, editable, onClose, onSaved }) {
    const [selected, setSelected] = useState(atlas.recordings.slice());
    const [search, setSearch] = useState('');
    const [offset, setOffset] = useState(0);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const mounted = useRef(true);
    useEffect(() => () => { mounted.current = false; }, []);
    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        const timer = setTimeout(() => {
            atlasApi('atlas_recordings?' + new URLSearchParams({ search, offset, limit: 30 }), null, controller.signal)
                .then(data => { setItems(data.items); setTotal(data.total); setLoading(false); })
                .catch(e => { if (e.name !== 'AbortError') { setError(e.message); setLoading(false); } });
        }, 150);
        return () => { clearTimeout(timer); controller.abort(); };
    }, [search, offset]);
    const save = async () => {
        setBusy(true); setError('');
        try {
            const result = await atlasApi('atlas_save', { atlasId: atlas.atlasId, revision: atlas.revision, recordings: JSON.stringify(selected) });
            if (mounted.current) onSaved(result);
        } catch (e) { if (mounted.current) { setError(e.message); setBusy(false); } }
    };
    return <Modal className="atlasModal" show onHide={() => !busy && onClose()} size="lg" backdrop="static" centered>
        <Modal.Header closeButton={!busy}><Modal.Title>Atlas Maps — {atlas.atlasName}</Modal.Title></Modal.Header>
        <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            {!editable && <Alert variant="info">{['queued', 'running'].includes(atlas.status) ? 'Recordings are locked while this atlas is queued or running.' : 'You have view-only access.'}</Alert>}
            <div className="atlasPicker">
                <section aria-label="Available recordings">
                    <h5>Available recordings</h5>
                    <Form.Control aria-label="Search recordings" placeholder="Search recording names" value={search}
                        onChange={e => { setSearch(e.target.value); setOffset(0); }} />
                    <div className="atlasRecordingList">
                        {loading ? <Spinner animation="border" role="status"><span className="sr-only">Loading recordings</span></Spinner> :
                            <ListGroup>{items.map(item => <ListGroup.Item key={item.filename}>
                                <Button variant="link" disabled={!editable || busy || selected.includes(item.filename)}
                                    onClick={() => setSelected(current => current.includes(item.filename) ? current : [...current, item.filename])}
                                    aria-label={'Add ' + item.filename}>{item.filename}</Button>
                                {selected.includes(item.filename) && <small>Selected</small>}
                                {item.comment && <small className="d-block text-muted">{item.comment}</small>}
                            </ListGroup.Item>)}</ListGroup>}
                        {!loading && !items.length && <p>No matching recordings.</p>}
                    </div>
                    <div className="atlasPaging">
                        <Button variant="outline-secondary" disabled={offset === 0 || loading} onClick={() => setOffset(offset - 30)}>Previous recordings</Button>
                        <small>{total ? offset + 1 : 0}–{Math.min(offset + 30, total)} of {total}</small>
                        <Button variant="outline-secondary" disabled={offset + 30 >= total || loading} onClick={() => setOffset(offset + 30)}>Next recordings</Button>
                    </div>
                </section>
                <section aria-label="Selected recordings">
                    <h5>Selected recordings ({selected.length})</h5>
                    <div className="atlasRecordingList">
                        <ListGroup>{selected.map(name => <ListGroup.Item key={name} className="atlasSelected">
                            <span>{name}</span><Button variant="outline-danger" size="sm" disabled={!editable || busy}
                                aria-label={'Remove ' + name} onClick={() => setSelected(current => current.filter(value => value !== name))}>Remove</Button>
                        </ListGroup.Item>)}</ListGroup>
                        {!selected.length && <p>Select recordings from the list.</p>}
                    </div>
                    <small>Remove changes atlas membership. The original recording is kept.</small>
                </section>
            </div>
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" disabled={busy} onClick={onClose}>{editable ? 'Cancel' : 'Close'}</Button>
            {editable && <Button disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</Button>}</Modal.Footer>
    </Modal>;
}
